import type { CardView, DrawSource } from "../types/protocol";

export type FlightRole = "self" | "opponent";
export type FlightTone = "pickup" | "discard";

/**
 * Un vol relie deux repères du plateau plutôt que deux éléments vivants : la
 * carte tenue quitte le DOM à l'instant où elle s'envole, et une case de grille
 * a déjà changé de face quand sa carte est encore en l'air. Le repère est donc
 * résolu au décollage, et toujours sur un élément qui existe à ce moment-là.
 */
export type FlightAnchor =
  | { kind: "pile"; pile: DrawSource }
  | { kind: "hand"; playerId: string; isSelf: boolean }
  | { kind: "card"; playerId: string; isSelf: boolean; index: number };

/**
 * Cache posé sur une case le temps qu'une carte la quitte ou s'y pose. Sans
 * lui, l'état du serveur montrerait tout de suite le résultat du coup et la
 * carte volerait vers une case qui a déjà changé.
 */
export interface CardCover {
  playerId: string;
  index: number;
  /** `null` laisse la case vide : sa carte est en l'air. */
  face: CardView | null;
  /** Seconde, comptée depuis le début du groupe, où le cache s'efface. */
  fadesAt: number;
}

/** Cache tel que le rendent les grilles : `active` dit si son vol est parti. */
export interface ActiveCover extends CardCover {
  key: string;
  active: boolean;
}

export interface CardFlight {
  id: string;
  role: FlightRole;
  tone: FlightTone;
  playerId: string;
  from: FlightAnchor;
  to: FlightAnchor;
  /** Face montrée à l'arrivée ; `null` garde le dos apparent tout le vol. */
  value: number | null;
  /** Le vol part dos visible et se retourne à mi-course. */
  startsHidden: boolean;
  /** Décollage différé, pour une colonne qui s'envole carte après carte. */
  delay: number;
  covers: CardCover[];
  /** Sommet que la défausse doit garder tant que la carte n'a pas atterri. */
  discardTopDuring?: { value: number | null };
}

/**
 * Deux rythmes pour un même geste : on sait ce qu'on vient de faire, il suffit
 * de voir la carte partir ; le coup d'en face, lui, doit rester lisible depuis
 * l'autre bout de la table.
 */
export const FLIGHT_DURATION: Record<FlightRole, number> = { self: 0.52, opponent: 0.9 };

/**
 * En mouvement réduit, la carte ne traverse pas la table : elle se signale sur
 * sa case d'arrivée. Ce signal est bref — le prolonger le temps d'un vol
 * laisserait une carte immobile clignoter presque une seconde.
 */
export const REDUCED_MOTION_DURATION = 0.3;

/** Part de la course où la carte touche sa cible ; le reste n'est qu'un fondu. */
const LANDING_RATIO = 0.82;
const CARD_RATIO = 91 / 65;

export const landingAfter = (role: FlightRole, delay = 0): number =>
  delay + FLIGHT_DURATION[role] * LANDING_RATIO;

export const flightLanding = (flight: CardFlight): number =>
  landingAfter(flight.role, flight.delay);

export const flightSpan = (flight: CardFlight): number =>
  flight.delay + FLIGHT_DURATION[flight.role];

/**
 * Les vols sont joués par l'API d'animation du navigateur, et non par la boucle
 * JavaScript de framer-motion. Cinq valeurs de transformation étaient
 * recalculées et réécrites à chaque image, sur le fil principal, pendant les
 * neuf dixièmes de seconde les plus chargés du jeu — trois cartes partent
 * ensemble quand une colonne s'aligne. Confiées au compositeur, elles ne lui
 * coûtent plus rien.
 *
 * Transformation et opacité voyagent sur deux pistes séparées parce que framer
 * ne les traitait pas de la même façon, et que le vol y perdrait sa forme : les
 * transformations étaient interpolées en JavaScript, qui étale la courbe sur
 * chaque intervalle de `times` ; l'opacité, elle, faisait partie des valeurs
 * accélérées par le navigateur et recevait la courbe sur la course entière.
 * Sous une courbe aussi marquée, la différence n'a rien d'anecdotique — la
 * carte se dissout dès le tiers du trajet au lieu de rester pleine jusqu'à
 * l'atterrissage. Une même animation ne peut pas porter les deux régimes.
 */
const EASE = "cubic-bezier(.22, .72, .2, 1)";

/**
 * Courbe implicite de framer pour une valeur animée sans `ease` : elle vient
 * d'`animateMotionValue`, qui sème `easeOut` avant d'étaler la transition — et
 * non du défaut `easeInOut` du générateur, jamais atteint depuis un composant.
 */
const EASE_DEFAULT = "ease-out";

/** Transformation d'une étape, dans l'ordre même qu'écrivait framer-motion. */
const transformAt = (x: number, y: number, scale: number, rotate: number) =>
  `translateX(${x}px) translateY(${y}px) scale(${scale}) rotate(${rotate}deg)`;

/**
 * Étapes du trajet, reprises une à une de ce qu'interpolait framer : mêmes
 * instants, mêmes valeurs, et la courbe portée par chaque intervalle.
 */
export const flightKeyframes = (path: FlightPath, reducedMotion: boolean): Keyframe[] => {
  const [startX, curveX, endX] = path.x;
  const [startY, curveY, endY] = path.y;
  const [startScale, peakScale, endScale] = path.scale;
  const landed = transformAt(endX, endY, endScale, 0);

  // Mouvement réduit : la carte ne traverse pas l'écran, elle se signale sur
  // place. Le repère reste celui de l'arrivée.
  if (reducedMotion) return [{ offset: 0, transform: landed }, { offset: 1, transform: landed }];

  return [
    { offset: 0, transform: transformAt(startX, startY, startScale, path.tilt), easing: EASE },
    {
      offset: 0.2,
      transform: transformAt(curveX, curveY, peakScale, -path.tilt * 1.5),
      easing: EASE,
    },
    { offset: 0.82, transform: landed, easing: EASE },
    { offset: 1, transform: landed },
  ];
};

/**
 * L'apparition puis l'effacement, sur leur piste propre : la courbe est ici
 * posée sur la course entière, comme le navigateur le faisait pour framer.
 */
export const opacityKeyframes = (reducedMotion: boolean): Keyframe[] =>
  reducedMotion
    ? [
        { offset: 0, opacity: 0 },
        { offset: 0.45, opacity: 1 },
        { offset: 1, opacity: 0 },
      ]
    : [
        { offset: 0, opacity: 0 },
        { offset: 0.2, opacity: 1 },
        { offset: 0.82, opacity: 1 },
        { offset: 1, opacity: 0 },
      ];

/** Courbe globale de la piste d'opacité, selon le régime de mouvement. */
export const opacityEasing = (reducedMotion: boolean): string =>
  reducedMotion ? EASE_DEFAULT : EASE;

/** La carte part dos visible et se retourne à mi-course. */
export const FLIP_KEYFRAMES: Keyframe[] = [
  { offset: 0, transform: "rotateY(0deg)", easing: EASE_DEFAULT },
  { offset: 0.28, transform: "rotateY(0deg)", easing: EASE_DEFAULT },
  { offset: 0.56, transform: "rotateY(180deg)", easing: EASE_DEFAULT },
  { offset: 1, transform: "rotateY(180deg)" },
];

/**
 * Minutage d'un vol. Fonction à part, et non objet construit au vol dans le
 * composant : c'est le seul endroit où la durée du mouvement réduit se
 * distingue de celle d'un trajet, et cette distinction se teste.
 */
export const flightTiming = (
  flight: Pick<CardFlight, "role" | "delay">,
  reducedMotion: boolean,
): KeyframeAnimationOptions => ({
  // Le signal du mouvement réduit est bref par nature : il annonce le coup sur
  // place au lieu de faire traverser l'écran à la carte, et n'a donc pas à
  // durer le temps d'un trajet.
  duration: (reducedMotion ? REDUCED_MOTION_DURATION : FLIGHT_DURATION[flight.role]) * 1000,
  delay: flight.delay * 1000,
  // `both` tient la carte sur sa première étape pendant le retard au décollage,
  // puis sur la dernière une fois posée.
  fill: "both",
});

interface FlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface FlightPath {
  width: number;
  height: number;
  /** Départ, sommet de la courbe, arrivée — coin haut-gauche de la carte. */
  x: [number, number, number];
  y: [number, number, number];
  scale: [number, number, number];
  tilt: number;
}

/** Coin haut-gauche d'une carte `width`×`height` centrée sur `rect`. */
const centeredOn = (rect: FlightRect, width: number, height: number) => ({
  x: rect.left + rect.width / 2 - width / 2,
  y: rect.top + rect.height / 2 - height / 2,
});

/**
 * Défilement à appliquer pour ramener un plateau au centre de la bande, ou
 * `null` s'il y est déjà — écrire une position identique à celle en place
 * annulerait un défilement lissé en cours et ferait sauter la bande.
 */
export const stripScrollTarget = (
  strip: HTMLElement,
  board: HTMLElement,
): number | null => {
  if (strip.scrollWidth <= strip.clientWidth) return null;
  const centeredLeft = board.offsetLeft - (strip.clientWidth - board.offsetWidth) / 2;
  const target = Math.max(0, Math.min(centeredLeft, strip.scrollWidth - strip.clientWidth));
  return Math.abs(strip.scrollLeft - target) < 2 ? null : target;
};

const opponentBoard = (playerId: string): HTMLElement | null => {
  const board = document.querySelector<HTMLElement>(
    `.opponent-board[data-player-id="${CSS.escape(playerId)}"]`,
  );
  if (!board) return null;

  // Les plateaux défilent horizontalement sur mobile : l'acteur revient dans la
  // zone visible avant qu'on ne mesure quoi que ce soit sur lui.
  const strip = board.closest<HTMLElement>(".opponents-strip");
  const target = strip ? stripScrollTarget(strip, board) : null;
  if (strip && target !== null) strip.scrollTo({ left: target, behavior: "auto" });
  return board;
};

/** Place de la pastille adverse, même avant qu'elle ne soit affichée. */
const opponentHandRect = (board: HTMLElement): FlightRect => {
  const badge = board.querySelector<HTMLElement>(".opponent-held-card__card");
  if (badge) return badge.getBoundingClientRect();
  const rect = board.getBoundingClientRect();
  const width = Math.max(20, rect.width * 0.16);
  return {
    left: rect.right - width - 12,
    top: rect.bottom - width * CARD_RATIO - 9,
    width,
    height: width * CARD_RATIO,
  };
};

const anchorRect = (anchor: FlightAnchor): FlightRect | null => {
  if (anchor.kind === "pile") {
    return (
      document
        .querySelector<HTMLElement>(`[data-draw-source="${anchor.pile}"] .pile-card`)
        ?.getBoundingClientRect() ?? null
    );
  }

  if (anchor.isSelf) {
    const element =
      anchor.kind === "hand"
        ? document.querySelector<HTMLElement>(".held-anchor")
        : document.querySelector<HTMLElement>(`.own-board [data-card-index="${anchor.index}"]`);
    return element?.getBoundingClientRect() ?? null;
  }

  const board = opponentBoard(anchor.playerId);
  if (!board) return null;
  if (anchor.kind === "hand") return opponentHandRect(board);
  return (
    board
      .querySelector<HTMLElement>(`[data-card-index="${anchor.index}"]`)
      ?.getBoundingClientRect() ?? null
  );
};

/**
 * Trajet réel entre deux repères : la carte part à la taille de sa source et
 * arrive à celle de sa cible, en passant par un arc qui la fait exister au
 * milieu du plateau plutôt que glisser en ligne droite.
 */
export const measureFlight = (flight: CardFlight): FlightPath | null => {
  const source = anchorRect(flight.from);
  const destination = anchorRect(flight.to);
  if (!source || !destination || source.width === 0 || destination.width === 0) return null;

  // Une carte partie d'une mini-grille resterait illisible à sa taille d'origine.
  const width = Math.min(76, Math.max(34, source.width));
  const height = width * CARD_RATIO;
  const start = centeredOn(source, width, height);
  const end = centeredOn(destination, width, height);
  const startScale = source.width / width;
  const endScale = Math.max(0.2, destination.width / width);
  const distanceX = end.x - start.x;
  const distanceY = end.y - start.y;
  const length = Math.max(1, Math.hypot(distanceX, distanceY));
  const lift = Math.min(84, Math.max(22, length * 0.16));

  return {
    width,
    height,
    x: [start.x, start.x + distanceX * 0.5 - (distanceY / length) * lift, end.x],
    y: [start.y, start.y + distanceY * 0.5 + (distanceX / length) * lift, end.y],
    scale: [startScale, Math.max(startScale, endScale) * 1.12, endScale],
    tilt: distanceX >= 0 ? 5 : -5,
  };
};
